import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Flame, Crown, BookOpen, CheckCircle2, Award, User, Mail, Phone, Pencil, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { BookCard, EmptyState, AuthorCard } from "@/components/cards";

function Card({ children, className = "" }) {
  return <section className={`rounded-3xl border border-border surface-2 p-6 md:p-8 ${className}`}>{children}</section>;
}

export default function Profile() {
  const { user, setUser } = useApp();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});

  const load = () => api.get("/me/profile-data").then((r) => { setD(r.data); setForm(r.data.user); });
  useEffect(() => {
    if (user === null) { nav("/login"); return; }
    if (user) load();
  }, [user]); // eslint-disable-line

  if (!user || !d) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/auth/profile", { name: form.name, phone: form.phone });
      setUser(data); setEdit(false); toast.success("Profile updated");
    } catch (e) { toast.error(apiError(e)); }
  };

  const streak = d.streak.streak || {};
  const nextM = d.streak.next_milestone;
  const curM = d.streak.current_milestone;
  const completed = d.library.completed || [];
  const reading = d.library.reading || [];

  // streak calendar last 35 days
  const today = new Date();
  const days = Array.from({ length: 35 }, (_, i) => {
    const dt = new Date(today); dt.setDate(today.getDate() - (34 - i));
    return { key: dt.toISOString().slice(0, 10) };
  });
  const historySet = new Set(streak.history || []);

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10 space-y-6 fade-in">
      <h1 className="text-4xl md:text-5xl font-display font-bold">My Profile</h1>

      {/* PROFILE INFO */}
      <Card>
        <div className="flex items-start gap-6">
          <div className="h-24 w-24 rounded-full bg-primary text-primary-foreground grid place-items-center text-3xl font-display overflow-hidden shrink-0">
            {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : user.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1">
            {!edit ? (
              <>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-display font-bold">{user.name}</h2>
                  <button onClick={() => setEdit(true)} data-testid="edit-profile-btn" className="p-1.5 rounded-lg hover:bg-secondary"><Pencil className="h-4 w-4" /></button>
                </div>
                <p className="flex items-center gap-2 text-muted-foreground mt-2 text-sm"><Mail className="h-4 w-4" />{user.email}</p>
                <p className="flex items-center gap-2 text-muted-foreground mt-1 text-sm"><Phone className="h-4 w-4" />{user.phone || "No phone added"}</p>
                <span className="inline-block mt-3 text-xs px-3 py-1 rounded-full bg-secondary capitalize">{user.role}</span>
              </>
            ) : (
              <div className="space-y-3 max-w-md">
                <input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" className="w-full bg-background border border-border rounded-xl px-4 py-2.5" placeholder="Name" />
                <input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="edit-phone" className="w-full bg-background border border-border rounded-xl px-4 py-2.5" placeholder="Phone" />
                <div className="flex gap-2">
                  <button onClick={saveProfile} data-testid="save-profile-btn" className="px-5 py-2 rounded-full bg-primary text-primary-foreground font-medium">Save</button>
                  <button onClick={() => { setEdit(false); setForm(user); }} className="px-5 py-2 rounded-full border border-border">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-6">
        {/* STREAK */}
        <Card className="md:col-span-2">
          <h2 className="text-xl font-display font-bold flex items-center gap-2"><Flame className="h-5 w-5 text-primary" /> Reading Streak</h2>
          <div className="flex gap-8 mt-4">
            <div><p className="text-4xl font-display font-bold text-primary" data-testid="current-streak">{streak.current || 0}</p><p className="text-xs text-muted-foreground track-label mt-1">Current</p></div>
            <div><p className="text-4xl font-display font-bold">{streak.longest || 0}</p><p className="text-xs text-muted-foreground track-label mt-1">Longest</p></div>
          </div>
          <div className="grid grid-cols-[repeat(35,1fr)] gap-1 mt-6">
            {days.map((d2) => <div key={d2.key} title={d2.key} className={`aspect-square rounded-[3px] ${historySet.has(d2.key) ? "bg-primary" : "bg-secondary"}`} />)}
          </div>
          {nextM && (
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-1"><span className="text-muted-foreground">{curM ? curM.title : "Next reward"}</span><span className="text-primary">{nextM.title} · {nextM.days} days</span></div>
              <div className="h-2 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(100, ((streak.current || 0) / nextM.days) * 100)}%` }} /></div>
            </div>
          )}
        </Card>

        {/* PREMIUM STATUS */}
        <Card>
          <h2 className="text-xl font-display font-bold flex items-center gap-2"><Crown className="h-5 w-5 text-amber-400" /> Premium</h2>
          {d.premium_active ? (
            <div className="mt-4" data-testid="premium-status">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary/15 text-primary text-sm"><CheckCircle2 className="h-4 w-4" />Active</span>
              <p className="text-sm text-muted-foreground mt-3 capitalize">Plan: {user.premium_plan || "premium"}</p>
              {user.premium_expiry && <p className="text-sm text-muted-foreground">Renews: {new Date(user.premium_expiry).toLocaleDateString()}</p>}
              <Link to="/premium" className="inline-block mt-4 text-sm text-primary">Manage Premium →</Link>
            </div>
          ) : (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">You're on the free plan. Upgrade for ad-free, unlimited reading.</p>
              <Link to="/premium" data-testid="upgrade-premium-btn" className="inline-block mt-4 px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-medium text-sm">Upgrade to Premium</Link>
            </div>
          )}
        </Card>
      </div>

      {/* REWARDS */}
      <Card>
        <h2 className="text-xl font-display font-bold flex items-center gap-2"><Award className="h-5 w-5 text-primary" /> Rewards</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
          {d.rewards.milestones.map((m) => (
            <div key={m.id} data-testid={`reward-${m.days}`} className={`text-center p-4 rounded-2xl border ${m.earned ? "border-primary bg-primary/10" : "border-border surface-2 opacity-70"}`}>
              <div className="text-3xl">{m.badge || "🏅"}</div>
              <p className="font-semibold text-sm mt-2">{m.title}</p>
              <p className="text-xs text-muted-foreground">{m.days} days</p>
              {!m.earned && <div className="h-1 bg-secondary rounded-full mt-2 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${m.progress}%` }} /></div>}
              {m.earned && <span className="text-[10px] text-primary track-label">Earned</span>}
            </div>
          ))}
        </div>
      </Card>

      {/* READING PROGRESS */}
      <Card>
        <h2 className="text-xl font-display font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> Reading Progress</h2>
        {reading.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5">
            {reading.map((b) => <div key={b.id}><BookCard book={b} /><div className="h-1 bg-secondary rounded-full mt-1.5 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${b.progress?.percent || 0}%` }} /></div></div>)}
          </div>
        ) : <p className="text-muted-foreground mt-4">You haven't started any stories yet. <Link to="/discover" className="text-primary">Discover something →</Link></p>}
      </Card>

      {/* COMPLETED */}
      <Card>
        <h2 className="text-xl font-display font-bold flex items-center gap-2"><Trophy className="h-5 w-5 text-primary" /> Completed Books</h2>
        {completed.length ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-5">
            {completed.map((b) => <div key={b.id}><BookCard book={b} />{b.progress?.completed_at && <p className="text-[11px] text-muted-foreground mt-1">Finished {new Date(b.progress.completed_at).toLocaleDateString()}</p>}</div>)}
          </div>
        ) : <p className="text-muted-foreground mt-4">No completed books yet — your finished stories will appear here.</p>}
      </Card>
      {/* FOLLOWED AUTHORS */}
      <Card>
        <h2 className="text-xl font-display font-bold flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Authors You Follow</h2>
        {d.following?.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-5">
            {d.following.map((a) => <AuthorCard key={a.id} author={a} />)}
          </div>
        ) : <p className="text-muted-foreground mt-4">You're not following any authors yet. <Link to="/authors" className="text-primary">Discover authors →</Link></p>}
      </Card>
    </div>
  );
}
